import { DeepPartial } from 'react-hook-form';
import { Namespace, TFunction } from 'react-i18next';
import { Description, DescriptionTranslation } from 'models/Descriptions';
import * as Yup from 'yup';
import { Channel } from 'models/Channels';
import { typedEntries } from 'utils/funcs';
import { isRequiredAtPropertyLevel } from './PropertySettingsDescriptionsTabFormFields.utils';
import { DescriptionFields } from './PropertySettingsDescriptionsTab.types';

export const AIRBNB_MAX_CHARS = 65_535;
export const BOOKING_DOT_COM_SUMMARY_MAX_CHARS = 2_000;
export const DESCRIPTION_FIELD_MAX_CHARS = 49_150;
export const SHORT_SUMMARY_MAX_CHARS = 250;
export const SHORT_SUMMARY_MIN_CHARS_IF_VRBO_ENABLED = 20;
export const SUMMARY_MAX_CHARS = 500;
export const NAME_MAX_CHARS = 50;
export const NAME_MIN_CHARS_IF_VRBO_ENABLED = 3;

const isValidForAirbnb = ({
  access,
  interaction,
  neighbourhood,
  notes,
  shortSummary,
  space,
  transit,
}: Partial<Description>) => {
  const totalCharacters = [
    access,
    interaction,
    neighbourhood,
    notes,
    shortSummary,
    space,
    transit,
  ]
    .filter((field) => field != null)
    .reduce((acc, field) => acc + (field?.length ?? 0), 0);

  return totalCharacters <= AIRBNB_MAX_CHARS;
};

export const propertySettingsDescriptionsTabSchema = ({
  activeChannelsAtPropertyLevel,
  t,
}: {
  activeChannelsAtPropertyLevel: Channel[];
  t: TFunction<Namespace<'en'>>;
}) => {
  const isVrboActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.homeaway,
  );
  const isAirbnbActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.airbnb,
  );

  const isRequired = (fieldName: DescriptionFields) => {
    return isRequiredAtPropertyLevel(activeChannelsAtPropertyLevel, fieldName);
  };

  const airbnbValidationObject = {
    name: 'combinedCharacterCount',
    // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    test: (_, testContext) => {
      return isValidForAirbnb(testContext.parent);
    },
    message: t('pageProperty.descriptions.airbnbCombinedCharacterCountError'),
  };

  return Yup.object({
    access: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    bookingDotComSummary: Yup.string()
      .optional()
      .nullable()
      .when('isApplyADifferentLongDescriptionEnabled', {
        is: true,
        then: (schema) => schema.required(),
      })
      .transform((value) =>
        value ? value.substring(0, BOOKING_DOT_COM_SUMMARY_MAX_CHARS) : value,
      ),
    houseManual: Yup.string()
      .optional()
      .nullable()
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    interaction: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    isApplyADifferentLongDescriptionEnabled: Yup.boolean().optional(),
    locale: Yup.string(),
    name: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isVrboActiveAtPropertyLevel,
        then: Yup.string()
          .required()
          .min(
            NAME_MIN_CHARS_IF_VRBO_ENABLED,
            t('pageProperty.descriptions.vrboNameMinLength'),
          ),
      })
      .when([], {
        is: () => isRequired('name'),
        then: (schema) => schema.required(),
      })
      .transform((value) =>
        value ? value.substring(0, NAME_MAX_CHARS) : value,
      ),
    neighbourhood: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    notes: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    shortSummary: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isVrboActiveAtPropertyLevel,
        then: Yup.string()
          .required()
          .min(
            SHORT_SUMMARY_MIN_CHARS_IF_VRBO_ENABLED,
            t('pageProperty.descriptions.vrboShortSummaryMinLength'),
          ),
      })
      .when([], {
        is: () => isRequired('shortSummary'),
        then: (schema) => schema.required(),
      })
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, SHORT_SUMMARY_MAX_CHARS) : value,
      ),
    space: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
    summary: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isRequired('summary'),
        then: (schema) => schema.required(),
      })
      .transform((value) =>
        value ? value.substring(0, SUMMARY_MAX_CHARS) : value,
      ),
    transit: Yup.string()
      .optional()
      .nullable()
      .when([], {
        is: () => isAirbnbActiveAtPropertyLevel,
        then: (schema) => schema.test(airbnbValidationObject),
      })
      .transform((value) =>
        value ? value.substring(0, DESCRIPTION_FIELD_MAX_CHARS) : value,
      ),
  });
};

const isFieldEmpty = (field?: string | null) => !field || field.trim() === '';

/**
 * This will cast a translation to a description, following a few rules:
 * - Prefer original description fields over translation fields, if they're not
 *   empty;
 * - If Vrbo is enabled and shortSummary has less than 20 characters, it'll
 *   automatically add `.` (periods) until the string meets the requirement;
 * - If Airbnb is enabled and the sum of lengths of all translated fields
 *   surpasses the AIRBNB_MAX_CHARS, then it'll evenly distribute the remaining
 *   length among the fields that were empty in the original description;
 *
 * This is all to avoid the most we can having translations that makes a
 * description form invalid, so that the user has less things to adjust (or even
 * none).
 */
export const transformDescriptionBasedOnTranslation = ({
  originalDescription: _originalDescription,
  translation,
  activeChannelsAtPropertyLevel,
  t,
}: {
  originalDescription?: Partial<Description>;
  translation: DescriptionTranslation;
  activeChannelsAtPropertyLevel: Channel[];
  t: TFunction<Namespace<'en'>>;
}): Description => {
  const isVrboActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.homeaway,
  );
  const isAirbnbActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.airbnb,
  );
  const schema = propertySettingsDescriptionsTabSchema({
    activeChannelsAtPropertyLevel,
    t,
  });

  // Casts the translation object to the schema to ensure that all fields are
  // present and of the correct type, besides capping the length of the fields.
  const {
    isApplyADifferentLongDescriptionEnabled:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isisApplyADifferentLongDescriptionEnabledTranslation,
    ...castedTranslation
  } = schema.cast(translation);
  const {
    isApplyADifferentLongDescriptionEnabled:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isisApplyADifferentLongDescriptionEnabledOriginal,
    ...originalDescription
  } = schema.cast(_originalDescription);

  // Creates a description object based on the original description but
  // overriding each empty field with its translation.
  const updatedDescription: Partial<Description> = typedEntries(
    originalDescription,
  ).reduce(
    (result, [key, value]) => {
      if (isFieldEmpty(value) && !isFieldEmpty(castedTranslation[key])) {
        return { ...result, [key]: castedTranslation[key] };
      }
      return result;
    },
    { ...originalDescription },
  );

  // If original shortSummary was empty and Vrbo is active, caps the translated
  // shortSummary to 20 chars.
  if (
    isFieldEmpty(originalDescription.shortSummary) &&
    isVrboActiveAtPropertyLevel &&
    updatedDescription.shortSummary &&
    updatedDescription.shortSummary.length < 20
  ) {
    updatedDescription.shortSummary = updatedDescription.shortSummary?.padEnd(
      20,
      '.',
    );
  }

  if (isAirbnbActiveAtPropertyLevel && !isValidForAirbnb(castedTranslation)) {
    const shortSummaryLength = updatedDescription.shortSummary?.length ?? 0;
    const airbnbRelatedFields = [
      'space',
      'access',
      'interaction',
      'neighbourhood',
      'transit',
      'notes',
    ] as const;

    const translatedAirbnbFields = airbnbRelatedFields.filter(
      (key) => !isFieldEmpty(castedTranslation[key]),
    );

    const emptyFields = translatedAirbnbFields.filter((field) =>
      isFieldEmpty(originalDescription[field]),
    );
    const nonEmptyFields = translatedAirbnbFields.filter(
      (field) => !isFieldEmpty(originalDescription[field]),
    );

    const nonEmptyFieldsLength = nonEmptyFields.reduce(
      (acc, field) => acc + (originalDescription[field]?.length ?? 0),
      0,
    );

    const remainingCharacters =
      AIRBNB_MAX_CHARS - shortSummaryLength - nonEmptyFieldsLength;
    const fieldCap = Math.floor(remainingCharacters / emptyFields.length);

    emptyFields.forEach((field) => {
      updatedDescription[field] = (updatedDescription[field] ?? '').substring(
        0,
        fieldCap,
      );
    });
  }

  return updatedDescription as Description;
};

export type PropertySettingsDescriptionsTabFormValues = DeepPartial<
  Yup.InferType<ReturnType<typeof propertySettingsDescriptionsTabSchema>>
>;

export const getFormDefaultValues = (
  description?: Partial<Description>,
): PropertySettingsDescriptionsTabFormValues => {
  return {
    access: description?.access,
    bookingDotComSummary: description?.bookingDotComSummary,
    houseManual: description?.houseManual,
    interaction: description?.interaction,
    isApplyADifferentLongDescriptionEnabled: !isFieldEmpty(
      description?.bookingDotComSummary,
    ),
    locale: description?.locale,
    name: description?.name,
    neighbourhood: description?.neighbourhood,
    notes: description?.notes,
    shortSummary: description?.shortSummary,
    space: description?.space,
    summary: description?.summary,
    transit: description?.transit,
  };
};
